import { CrownFrame } from "@/components/CrownFrame";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Swords, Shield, Zap, Wind, Leaf, FlaskConical, Heart, Sword, ShieldAlert, Sparkles, Snowflake } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useGame } from "@/context/GameContext";
import { Bar } from "@/components/Bar";
import BoostBadges from "@/components/BoostBadges";
import BattleResult from "@/components/BattleResult";

const BGS = { frostpine: "/art/battle_bg.webp", ashen: "/art/ashen_bg.webp" };
const ACTION_ICONS = { bite: Swords, claw: Wind, howl: Zap, guard: Shield };
const INTENT = {
  attack: { icon: Sword, cls: "border-red-500/60 text-red-200 bg-red-950/70", t: "Attack" },
  heavy: { icon: ShieldAlert, cls: "border-red-400 text-white bg-red-600/80 animate-pulse", t: "HEAVY" },
  defend: { icon: Shield, cls: "border-sky-500/60 text-sky-200 bg-sky-950/70", t: "Defend" },
  buff: { icon: Zap, cls: "border-amber-500/60 text-amber-200 bg-amber-950/70", t: "Empower" },
  heal: { icon: Heart, cls: "border-emerald-500/60 text-emerald-200 bg-emerald-950/70", t: "Heal" },
};

function Floaters({ items }) {
  return items.map((f) => (
    <span key={f.id} className={`dmg-float absolute left-1/2 top-1/4 font-mono-game font-bold pointer-events-none z-20 ${f.cls}`} style={{ marginLeft: f.dx }}>{f.text}</span>
  ));
}

function Fighter({ side, name, image, hp, max, flash, floaters, tier, children }) {
  return (
    <div className={`relative flex flex-col ${side === "enemy" ? "items-end" : "items-start"}`}>
      <div className="w-full max-w-[260px] mb-3">
        <p className="font-display text-sm sm:text-base text-amber-50 mb-1 truncate">{name}</p>
        <Bar testid={`${side}-hp-bar`} label="HP" value={hp} max={max} color={side === "enemy" ? "#EF4444" : "#22C55E"} />
        {children}
      </div>
      <div className="relative">
        <CrownFrame tier={tier} className="rounded-3xl"><img src={image} alt={name} className={`breathe w-36 h-36 sm:w-56 sm:h-56 lg:w-64 lg:h-64 object-cover ${tier === "platinum" ? "rounded-3xl" : "fade-mask"} ${flash ? "hit-flash" : ""}`} /></CrownFrame>
        <Floaters items={floaters} />
      </div>
    </div>
  );
}

export default function Battle() {
  const { id } = useParams();
  const nav = useNavigate();
  const { game, refresh } = useGame();
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);
  const [shake, setShake] = useState(false);
  const [flash, setFlash] = useState({ player: false, enemy: false });
  const [floats, setFloats] = useState({ player: [], enemy: [] });
  const logRef = useRef(null);
  const seq = useRef(0);

  useEffect(() => { api.get(`/battle/${id}`).then(({ data }) => setB(data)).catch(() => nav("/journey")); }, [id, nav]);
  useEffect(() => { logRef.current?.scrollTo({ top: 99999, behavior: "smooth" }); }, [b?.log?.length]);

  const play = (events) => {
    events.forEach((ev, i) => setTimeout(() => {
      const fid = ++seq.current;
      const text = ev.kind === "damage" ? `-${ev.value}${ev.crit ? "!" : ""}` : ev.kind === "heal" ? `+${ev.value}` : ev.value;
      const cls = ev.kind === "damage" ? (ev.crit || ev.heavy ? "text-4xl text-fuchsia-400" : "text-2xl text-red-300") : ev.kind === "heal" ? "text-2xl text-emerald-300" : "text-lg text-sky-200";
      setFloats((f) => ({ ...f, [ev.target]: [...f[ev.target], { id: fid, text, cls, dx: (i % 3 - 1) * 24 }] }));
      setTimeout(() => setFloats((f) => ({ ...f, [ev.target]: f[ev.target].filter((x) => x.id !== fid) })), 1200);
      if (ev.kind === "damage") {
        setFlash((f) => ({ ...f, [ev.target]: true }));
        setTimeout(() => setFlash((f) => ({ ...f, [ev.target]: false })), 350);
        if (ev.heavy || ev.crit || ev.value >= 20) { setShake(true); setTimeout(() => setShake(false), 420); }
      }
    }, i * 380));
  };

  const send = async (path, payload) => {
    setBusy(true);
    try {
      const { data } = await api.post(path, payload);
      play(data.events || []);
      setB(data.battle);
      if (data.battle.status !== "active" || payload?.action === "item" || path.endsWith("revive")) await refresh();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const act = (action, item) => send(`/battle/${id}/action`, { action, item });

  if (!b) return <div className="min-h-[60vh] grid place-items-center text-slate-500 font-display">The wind carries a scent…</div>;
  const { wolf, skills, basic_actions, looks, boosts, settings } = game;
  const p = b.player, e = b.enemy, intent = INTENT[b.intent.kind];
  const active = b.status === "active";
  const inv = wolf.inventory;

  return (
    <main className={`relative min-h-[calc(100vh-4rem)] overflow-hidden ${shake ? "animate-shake" : ""}`} data-testid="battle-screen">
      <img src={b.bg || BGS[b.region] || BGS.frostpine} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#061016] via-[#061016]/50 to-[#061016]/70" />
      <div className="relative z-10 max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <span data-testid="battle-turn" className="font-mono-game text-xs text-slate-400">TURN {b.turn}{e.boss ? " · BOSS" : e.elite ? " · ELITE" : ""}{b.phase === 2 ? " · FRENZY" : ""}</span>
          <BoostBadges boosts={boosts} settings={settings} art={game.badge_art} compact />
        </div>

        <div className="grid grid-cols-2 gap-4 items-end min-h-[300px] sm:min-h-[380px]">
          <Fighter side="player" name={wolf.character ? `${wolf.name} · ${wolf.character.name}` : wolf.name} image={game.avatar} tier={game.wallet?.mwp?.tier} hp={p.hp} max={p.max_hp} flash={flash.player} floaters={floats.player}>
            <div className="flex items-center gap-1 mt-2" data-testid="player-spirit">
              {[...Array(5)].map((_, i) => <span key={i} className={`w-4 h-4 rounded-full border ${i < p.spirit ? "bg-blue-500 border-blue-300 shadow-[0_0_8px_#3b82f6]" : "border-slate-700"}`} />)}
              <span className="ml-2 text-[10px] font-mono-game text-blue-300">SPIRIT</span>
            </div>
            {b.pack?.length > 0 && (
              <div className="flex items-center gap-1.5 mt-2" data-testid="battle-pack">
                {b.pack.map((m) => <img key={`${m.key}-${m.token_id}`} src={m.image} alt={m.name} title={m.name} className="w-9 h-9 rounded-full object-cover border-2 border-cyan-400/70" />)}
                <span className="text-[10px] font-mono-game text-cyan-300">PACK</span>
              </div>
            )}
            <div className="flex flex-wrap gap-1 mt-2 text-[10px] font-mono-game">
              {b.fx.howl > 0 && <span className="px-1.5 py-0.5 rounded bg-amber-900/60 text-amber-200">HOWL x{b.fx.howl}</span>}
              {b.fx.ironhide > 0 && <span className="px-1.5 py-0.5 rounded bg-sky-900/60 text-sky-200">IRONHIDE</span>}
              {b.fx.atk_tonic && <span className="px-1.5 py-0.5 rounded bg-fuchsia-900/60 text-fuchsia-200">ATK TONIC</span>}
              {b.fx.def_tonic && <span className="px-1.5 py-0.5 rounded bg-sky-900/60 text-sky-200">DEF TONIC</span>}
            </div>
          </Fighter>
          <Fighter side="enemy" name={e.name} image={e.image} hp={e.hp} max={e.max_hp} flash={flash.enemy} floaters={floats.enemy}>
            {active && (
              <div data-testid="enemy-intent" className={`mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs ${intent.cls}`}>
                <intent.icon className="w-4 h-4" />
                <span className="font-semibold">{b.intent.label}</span>
                {b.intent.estimate && <span className="font-mono-game" data-testid="enemy-intent-damage">~{b.intent.estimate}</span>}
              </div>
            )}
            <div className="flex flex-wrap gap-1 mt-2 text-[10px] font-mono-game justify-end">
              {b.fx.enemy_buff > 0 && <span className="px-1.5 py-0.5 rounded bg-red-900/60 text-red-200">ENRAGED +{Math.round(b.fx.enemy_buff * 100)}%</span>}
              {b.fx.weak > 0 && <span className="px-1.5 py-0.5 rounded bg-slate-800 text-slate-200">WEAKENED</span>}
              {b.fx.stunned && <span className="px-1.5 py-0.5 rounded bg-purple-900/60 text-purple-200">STUNNED</span>}
            </div>
          </Fighter>
        </div>

        <div className="grid lg:grid-cols-[1fr_300px] gap-4 mt-6">
          <div className="glass rounded-2xl p-4" data-testid="action-palette">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
              {Object.entries(basic_actions).map(([aid, a]) => {
                const Icon = ACTION_ICONS[aid];
                return (
                  <button key={aid} data-testid={`battle-action-${aid}`} disabled={!active || busy} onClick={() => act(aid)} title={a.desc}
                    className="flex items-center justify-center gap-2 py-3 rounded-lg border border-slate-700 bg-slate-900/70 hover:border-fuchsia-500 hover:bg-fuchsia-500/15 disabled:opacity-40 transition-colors text-sm font-semibold">
                    <Icon className="w-4 h-4 text-fuchsia-400" />{a.name}
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
              {wolf.equipped.map((sid) => {
                const s = skills[sid];
                return (
                  <button key={sid} data-testid={`battle-skill-${sid}`} disabled={!active || busy || p.spirit < s.cost} onClick={() => act(sid)} title={s.desc}
                    className={`text-left px-3 py-2 rounded-lg border ${s.nft ? "nft-glow border-amber-500/60" : "border-blue-700/60"} bg-blue-950/40 hover:bg-blue-900/50 disabled:opacity-40 transition-colors`}>
                    <span className="flex items-center justify-between text-sm font-semibold">
                      <span className="flex items-center gap-1.5">{s.nft ? <Sparkles className="w-3.5 h-3.5 text-amber-300" /> : <Snowflake className="w-3.5 h-3.5 text-sky-300" />}{s.name}</span>
                      <span className="font-mono-game text-xs text-blue-300">{s.cost}◆</span>
                    </span>
                    <span className="block text-[11px] text-slate-400 mt-0.5 truncate">{s.desc}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-2">
              {[["herb", Leaf, "Herb", "text-emerald-300"], ["atk_tonic", FlaskConical, "Atk Tonic", "text-fuchsia-300"], ["def_tonic", FlaskConical, "Def Tonic", "text-sky-300"]].map(([it, Icon, label, c]) => (
                <button key={it} data-testid={`battle-item-${it}`} disabled={!active || busy || !inv[it] || (it !== "herb" && b.fx[it])} onClick={() => act("item", it)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-700 text-xs hover:border-slate-400 disabled:opacity-35">
                  <Icon className={`w-3.5 h-3.5 ${c}`} />{label} <span className="font-mono-game text-slate-400">x{inv[it] || 0}</span>
                </button>
              ))}
              {active && (
                <button data-testid="battle-flee-btn" disabled={busy} onClick={async () => { const { data } = await api.post(`/battle/${id}/retreat`); toast(data.message); await refresh(); nav("/journey"); }}
                  className="ml-auto px-3 py-1.5 rounded-full text-xs text-slate-500 hover:text-red-300">Flee to den</button>
              )}
            </div>
          </div>
          <div ref={logRef} data-testid="battle-log" className="glass rounded-2xl p-4 h-48 lg:h-auto lg:max-h-64 overflow-y-auto text-xs space-y-1.5 font-mono-game text-slate-300">
            {b.log.map((l, i) => <p key={i} className={i === b.log.length - 1 ? "text-amber-100" : "text-slate-400"}>› {l}</p>)}
          </div>
        </div>
      </div>
      <BattleResult b={b} game={game} busy={busy} onRevive={(m) => send(`/battle/${id}/revive`, { method: m })}
        onRetreat={async () => { const { data } = await api.post(`/battle/${id}/retreat`); toast(data.message); await refresh(); nav("/journey"); }}
        onContinue={() => nav("/journey")} />
    </main>
  );
}
